import React from 'react';
import {
  Accordion as MUIAccordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Skeleton,
  Box,
} from '@mui/material';
import { ExpandLess, KeyboardArrowRight } from '@mui/icons-material';
import useStyles from './style';

/**
 * Hook that returns true once the element is within `rootMargin` of the viewport.
 * Once visible, it stays true (no un-mounting on scroll away) to preserve state.
 */
const useLazyVisible = (rootMargin = '200px') => {
  const ref = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!element || isVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return [ref, isVisible];
};

const ContentPlaceholder = () => (
  <Box sx={{ p: 2 }}>
    <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
    <Skeleton variant="rectangular" height={40} />
  </Box>
);

const Accordion = ({
  defaultExpanded = false,
  customizedTitle,
  title = '',
  content = '',
  showMore = true,
  subComponent,
  accordionSummaryStyle,
  summaryTitleVariant = 'h2',
  noGutterAccordion,
  stickySummary = false,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const [lazyRef, isNearViewport] = useLazyVisible('400px');

  const handleChange = () => {
    setExpanded((prev) => !prev);
  };

  // Render content if: expanded AND (near viewport OR has been rendered before)
  const shouldRenderContent = expanded && isNearViewport;

  return (
    <MUIAccordion
      ref={lazyRef}
      className={`${noGutterAccordion ? classes.noGutterAccordion : ''}`}
      expanded={expanded}
      onChange={handleChange}
    >
      <AccordionSummary
        expandIcon={
          expanded ? (
            <ExpandLess className={classes.expandIcon} />
          ) : (
            <KeyboardArrowRight className={classes.expandIcon} />
          )
        }
        className={`${classes.summary} ${accordionSummaryStyle ? classes.collectionAccordionSummary : ''}${
          stickySummary ? ` ${classes.stickySummary}` : ''
        }`}
      >
        {!customizedTitle && (
          <Typography ml={2} data-testid="accordionTitle" variant={summaryTitleVariant} className={classes.title}>
            {title}
            {showMore && subComponent}
          </Typography>
        )}
        {customizedTitle && title}
      </AccordionSummary>
      <AccordionDetails data-testid="accordionContent" className={classes.details}>
        {shouldRenderContent ? content : <ContentPlaceholder />}
      </AccordionDetails>
    </MUIAccordion>
  );
};

export default React.memo(Accordion);
